import axios, { type AxiosResponse } from 'axios'
import BaseService from './BaseService'

const GRAPHQL_API = `${import.meta.env.VITE_HOTEL_SEARCH_GRAPHQL_API_URL}`
const GRAPHQL_CLIENT_NAME = 'trivago-business-studio-web'

const getCities = `query ($input: SearchSuggestionsInput!) {
  getSearchSuggestions(input: $input) {
    spellingCorrectedQuery
    unifiedSearchSuggestions {
      ... on ConceptSearchSuggestion {
        concept {
          translatedName {
            value
          }
          ... on Destination {
            locationLabel
          }
        }
      }
    }
  }
}`

const getPlatformLanguages = `query GetPlatformLanguages {
  getPlatformLanguages {
    languageTag {
      code
      language
    }
    label
    translatedLabel
    localizedLabel
    localizedTranslatedLabel
  }
}`

export interface FetchCitiesRequest {
  locale: string
  query: string
}

export interface DestinationConcept {
  translatedName: {
    value: string
  }
  locationLabel?: string
}

export interface CitySearchSuggestion {
  concept: DestinationConcept
}

export interface CitiesResponse {
  data: {
    getSearchSuggestions: {
      spellingCorrectedQuery: string | null
      unifiedSearchSuggestions: CitySearchSuggestion[]
    }
  }
}

export interface FetchPlatformLanguagesRequest {
  locale?: string
}

export interface PlatformLanguage {
  languageTag: {
    code: string
    language: string
  }
  label: string
  translatedLabel: string
  localizedLabel: string
  localizedTranslatedLabel: string
}

export interface PlatformLanguagesResponse {
  data: {
    getPlatformLanguages: PlatformLanguage[]
  }
}

class PropertyDetailsService extends BaseService {
  constructor() {
    super({
      baseUrl: GRAPHQL_API,
    })
  }

  fetchCities({ locale, query }: FetchCitiesRequest): Promise<AxiosResponse<CitiesResponse>> {
    return axios({
      url: this.baseUrl,
      method: 'POST',
      data: {
        query: getCities,
        variables: {
          input: {
            query: query,
            filters: {
              namespaces: ['DESTINATION'],
              destinationTypes: ['CITY'],
            },
          },
        },
      },
      headers: this.graphqlHeaders(locale, GRAPHQL_CLIENT_NAME),
    })
  }

  fetchPlatformLanguages({ locale }: FetchPlatformLanguagesRequest): Promise<AxiosResponse<PlatformLanguagesResponse>> {
    return axios({
      url: this.baseUrl,
      method: 'POST',
      data: {
        query: getPlatformLanguages,
      },
      headers: this.graphqlHeaders(locale || 'en-US', GRAPHQL_CLIENT_NAME),
    })
  }
}

export default new PropertyDetailsService()
